import styled, { css } from 'styled-components'

import { BackgroundLayer } from './styles'

type SplitOrderStatusTone = 'default' | 'warning' | 'danger' | 'success'

const getStatusToneStyles = (
  props: { theme: any; $statusTone?: SplitOrderStatusTone } & Record<string, any>
) => {
  switch (props.$statusTone) {
    case 'warning':
      return css`
        background-color: rgba(255, 184, 34, 0.12);
        color: ${props.theme.colors.mySin};
      `
    case 'danger':
      return css`
        background-color: rgba(253, 57, 122, 0.12);
        color: ${props.theme.colors.radicalRed};
      `
    case 'success':
      return css`
        background-color: rgba(29, 201, 183, 0.12);
        color: ${props.theme.colors.java};
      `
    default:
      return css`
        background-color: rgba(93, 120, 255, 0.12);
        color: ${props.theme.colors.dodgerBlue};
      `
  }
}

export const SplitOrdersCardContainer = styled(BackgroundLayer)`
  flex-direction: column;
  padding: 1.5rem;
  gap: 1rem;
`

export const SplitOrdersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  h3 {
    margin: 0 0 0.25rem;
    font-size: 1.25rem;
    color: ${(props) => props.theme.colors.mulledWine};
  }
`

export const SplitOrdersMeta = styled.div`
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.gray};

  a {
    color: ${(props) => props.theme.colors.dodgerBlue};
    font-weight: 600;
    text-decoration: none;

    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
`

export const SplitOrdersMetaSeparator = styled.span`
  display: inline-block;
  margin: 0 0.5rem;
  color: ${(props) => props.theme.colors.gray};
`

export const SplitOrderEntry = styled.div<{ 'data-current'?: boolean }>`
  border: 1px solid ${(props) => props.theme.colors.gallery};
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  ${(props) =>
    props['data-current'] &&
    css`
      border-color: ${props.theme.colors.dodgerBlue};
      box-shadow: 0px 0px 0px 2px rgba(93, 120, 255, 0.18);
    `}
`

export const SplitOrderEntryHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: center;

  .badges {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`

export const SplitOrderIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.mulledWine};

  .label {
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${(props) => props.theme.colors.gray};
  }

  a {
    color: ${(props) => props.theme.colors.dodgerBlue};
    text-decoration: none;
  }
`

export const SplitOrderBadge = styled.span<{
  $variant?: 'highlight' | 'subtle' | 'status' | 'disposition'
  $statusTone?: SplitOrderStatusTone
  $disposition?: 'SHIP' | 'BACKORDER' | 'CANCEL'
}>`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.35rem 0.6rem;
  background-color: ${(props) => props.theme.colors.whiteLilac};
  color: ${(props) => props.theme.colors.mulledWine};

  ${(props) =>
    props.$variant === 'highlight' &&
    css`
      background-color: ${props.theme.colors.dodgerBlue};
      color: ${props.theme.colors.white};
    `}

  ${(props) =>
    props.$variant === 'subtle' &&
    css`
      background-color: ${props.theme.colors.zircon};
      color: ${props.theme.colors.mulledWine};
    `}

  ${(props) => props.$variant === 'status' && getStatusToneStyles(props)}

  ${(props) =>
    props.$variant === 'disposition' &&
    props.$disposition === 'SHIP' &&
    css`
      background-color: rgba(29, 201, 183, 0.15);
      color: ${props.theme.colors.java};
    `}

  ${(props) =>
    props.$variant === 'disposition' &&
    props.$disposition === 'BACKORDER' &&
    css`
      background-color: rgba(255, 184, 34, 0.15);
      color: ${props.theme.colors.mySin};
    `}

  ${(props) =>
    props.$variant === 'disposition' &&
    props.$disposition === 'CANCEL' &&
    css`
      background-color: rgba(253, 57, 122, 0.15);
      color: ${props.theme.colors.radicalRed};
    `}
`

export const SplitOrderItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid ${(props) => props.theme.colors.gallery};
  border-radius: 6px;
  overflow: hidden;
  font-size: 0.875rem;

  thead {
    background-color: ${(props) => props.theme.colors.whiteLilac};

    th {
      text-transform: uppercase;
      letter-spacing: 0.04em;
      font-size: 0.75rem;
      color: ${(props) => props.theme.colors.gray};
      padding: 0.65rem 0.75rem;
      text-align: left;

      &:nth-child(3),
      &:nth-child(4) {
        text-align: center;
      }
    }
  }

  tbody {
    tr {
      border-top: 1px solid ${(props) => props.theme.colors.gallery};

      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 0.75rem;
      color: ${(props) => props.theme.colors.mulledWine};
      vertical-align: middle;

      &:first-child {
        font-weight: 600;
      }

      &:nth-child(3),
      &:nth-child(4) {
        text-align: center;
      }
    }
  }

  @media (max-width: 640px) {
    thead {
      display: none;
    }

    tbody,
    tr,
    td {
      display: block;
      width: 100%;
    }

    tr {
      border: 1px solid ${(props) => props.theme.colors.gallery};
      border-radius: 8px;
      margin-bottom: 0.75rem;
      padding: 0.5rem 0.75rem;
    }

    td {
      border: none;
      padding: 0.25rem 0;
      position: relative;

      &::before {
        content: attr(data-title);
        display: block;
        font-size: 0.75rem;
        color: ${(props) => props.theme.colors.gray};
        text-transform: uppercase;
        margin-bottom: 0.15rem;
      }
    }
  }
`

export const SplitOrderEmpty = styled.div`
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.gray};
  font-style: italic;
`

export const SplitOrdersActions = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`

export const SplitOrdersActionsButton = styled.button`
  appearance: none;
  border: 1px solid rgba(93, 120, 255, 0.35);
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.dodgerBlue};
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.4rem 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;

  svg {
    font-size: 0.9rem;
  }

  &:hover,
  &:focus {
    background-color: ${(props) => props.theme.colors.zircon};
  }
`

export const SplitOrdersActionsMenu = styled.ul`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  list-style: none;
  margin: 0;
  padding: 0.5rem 0;
  background-color: ${(props) => props.theme.colors.white};
  box-shadow: 0px 10px 30px rgba(82, 63, 105, 0.18);
  border: 1px solid ${(props) => props.theme.colors.gallery};
  border-radius: 6px;
  min-width: 220px;
  z-index: 5;

  li {
    display: flex;
    align-items: center;
  }

  a,
  button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.55rem 0.85rem;
    font-size: 0.875rem;
    color: ${(props) => props.theme.colors.mulledWine};
    text-decoration: none;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;

    svg {
      color: ${(props) => props.theme.colors.dodgerBlue};
    }

    &:hover,
    &:focus {
      background-color: ${(props) => props.theme.colors.zircon};
      color: ${(props) => props.theme.colors.dodgerBlue};
    }
  }

  button {
    font-weight: 600;
  }
`
